import React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
} from 'lucide-react';

import type {
  PassbookItem,
  PassbookPagination,
} from '../types';

interface WithdrawalTableProps {
  items: PassbookItem[];
  pagination: PassbookPagination | null;
  loading?: boolean;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRowClick: (transaction: PassbookItem) => void;
}

const formatCurrency = (value?: number | null) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (value?: string | null) => {
  if (!value) return '—';

  return new Date(value).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusClasses = (status?: string) => {
  const normalized = String(status || '').toLowerCase();

  if (
    ['completed', 'approved', 'success', 'successful', 'captured'].includes(
      normalized,
    )
  ) {
    return 'bg-emerald-50 text-emerald-700';
  }

  if (
    ['rejected', 'failed', 'cancelled', 'canceled'].includes(normalized)
  ) {
    return 'bg-red-50 text-red-700';
  }

  if (
    ['pending', 'initiated', 'processing'].includes(normalized)
  ) {
    return 'bg-amber-50 text-amber-700';
  }

  return 'bg-slate-100 text-slate-700';
};

const formatGateway = (gateway?: string | null) => {
  if (!gateway) return '—';

  return gateway
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const WithdrawalTable: React.FC<WithdrawalTableProps> = ({
  items,
  pagination,
  loading = false,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRowClick,
}) => {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Withdrawal History
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Player withdrawal requests and backend wallet deductions.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-600">
          Rows
          <select
            value={pageSize}
            disabled={loading}
            onChange={(event) =>
              onPageSizeChange(Number(event.target.value))
            }
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
          >
            {[10, 20, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              {[
                'Date & Time',
                'Amount',
                'Method',
                'Bank / Reference',
                'Status',
                'Action',
              ].map((heading) => (
                <th
                  key={heading}
                  className="whitespace-nowrap px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 bg-white">
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-5 py-12 text-center text-sm text-slate-500"
                >
                  Loading withdrawals...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-5 py-12 text-center text-sm text-slate-500"
                >
                  No withdrawal transactions found.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.id}
                  tabIndex={0}
                  role="button"
                  onClick={() => onRowClick(item)}
                  onKeyDown={(event) => {
                    if (
                      event.key === 'Enter' ||
                      event.key === ' '
                    ) {
                      event.preventDefault();
                      onRowClick(item);
                    }
                  }}
                  className="cursor-pointer transition hover:bg-blue-50/40 focus:bg-blue-50 focus:outline-none"
                >
                  <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-600">
                    {formatDate(item.created_at)}
                  </td>

                  <td className="whitespace-nowrap px-5 py-4 text-sm font-semibold text-red-600">
                    -{formatCurrency(Math.abs(Number(item.amount || 0)))}
                  </td>

                  <td className="whitespace-nowrap px-5 py-4 text-sm font-medium text-slate-700">
                    {formatGateway(item.gateway || 'bank transfer')}
                  </td>

                  <td className="max-w-[260px] px-5 py-4">
                    <p className="truncate font-mono text-sm font-semibold text-slate-800">
                      {item.transaction_id || item.id}
                    </p>

                    <p className="mt-1 text-xs text-slate-400">
                      {item.type === 'withdrawal'
                        ? 'Withdrawal Reference'
                        : 'Internal Reference'}
                    </p>
                  </td>

                  <td className="whitespace-nowrap px-5 py-4">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusClasses(
                        item.status,
                      )}`}
                    >
                      {item.status_display ||
                        item.status ||
                        'Unknown'}
                    </span>
                  </td>

                  <td className="whitespace-nowrap px-5 py-4">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onRowClick(item);
                      }}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && pagination.total_pages > 1 && (
        <div className="flex flex-col gap-3 border-t border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-500">
            Page {pagination.current_page} of{' '}
            {pagination.total_pages} ·{' '}
            {pagination.total_count.toLocaleString('en-IN')} withdrawals
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={loading || !pagination.has_previous}
              onClick={() =>
                onPageChange(
                  Math.max(1, pagination.current_page - 1),
                )
              }
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>

            <button
              type="button"
              disabled={loading || !pagination.has_next}
              onClick={() =>
                onPageChange(pagination.current_page + 1)
              }
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalTable;
